/**
 * Server Utilities Module
 * Provides core HTTP communication and data validation utilities for API interactions
 * 
 * Features:
 * - JSON validation for response parsing
 * - Unified API call handler with multiple content-type support
 * - Automatic authorization token management from localStorage
 * - Comprehensive error tracking and HTTP status handling
 * - Support for JSON and multipart/form-data request types
 * 
 * Dependencies:
 * - error_tracker.js for error logging and tracking
 */

import {trackError} from "./error_tracker"
// import { useNavigate } from "react-router-dom";
// const navigate = useNavigate();

/**
 * Validate if a string is valid JSON format
 * Safely attempts to parse a string as JSON and returns true only if successful
 * Used to validate API responses before processing
 * 
 * @param {string} text - The string to validate as JSON
 * @returns {boolean} True if the string is valid JSON, false if empty, null, or invalid format
 * 
 * @example
 * // Valid JSON
 * isValidJson('{"name": "John", "age": 30}') // Returns: true
 * 
 * @example
 * // Invalid JSON
 * isValidJson('{"name": "John"') // Returns: false
 * 
 * @example
 * // Empty string
 * isValidJson('') // Returns: false
 */
export function isValidJson(text) {
    if (!text) return false; // Return false if the text is empty
    try {
        JSON.parse(text); // Attempt to parse the string as JSON
        return true;
    } catch (error) {
        console.error("Invalid JSON:", error); // Log the parsing error
        return false;
    }
}

/**
 * Make a server API call using Fetch API with automatic authentication
 * Handles multiple HTTP methods and content types with built-in error tracking
 * Automatically includes authorization token from localStorage in request headers
 * 
 * Features:
 * - Automatic token extraction and Bearer token injection
 * - Distinguishes between access and refresh tokens based on endpoint
 * - Supports JSON and multipart/form-data request bodies
 * - 401 Unauthorized response redirects to login page
 * - Comprehensive error tracking with URL and method context
 * - Response validation and logging
 * 
 * Process:
 * 1. Extracts and parses auth token from localStorage ("jsondata" key)
 * 2. Selects refresh token for refresh-token and logout endpoints
 * 3. Constructs fetch request with appropriate headers and body
 * 4. Handles FormData for multipart/form-data content type
 * 5. Validates HTTP response status
 * 6. Parses and validates JSON response
 * 7. Tracks errors for debugging and monitoring
 * 
 * @async
 * @param {string} method - HTTP method: 'GET', 'POST', 'PUT', 'DELETE', 'PATCH'
 * @param {string} apiUrl - Full API endpoint URL (including domain and path)
 * @param {Object} [data] - Request body data (unused for GET requests)
 *                         For JSON: plain JavaScript object
 *                         For FormData: object with key-value pairs (files as File objects)
 * @param {string} [content_type='application/json'] - Content type for request
 *                                                    Options: 'application/json', 'multipart/form-data'
 * @param {string} [authToken=localStorage.getItem("jsondata")] - Authorization token (optional)
 *                                                                 Defaults to stored token from login
 * 
 * @returns {Promise<Object>} Parsed response data from server
 *                           Returns undefined on 401 (redirects to login)
 *                           Returns parsed JSON response on success
 *                           Returns undefined if error occurs (after tracking)
 * 
 * @throws Will not throw errors, instead tracks them via trackError()
 *         Errors include: network failures, invalid JSON, HTTP errors
 * 
 * @example
 * // Simple GET request
 * const documents = await apiCall('GET', 'https://api.example.com/documents');
 * 
 * @example
 * // POST request with JSON data
 * const newDocument = {
 *   title: 'Contract Review',
 *   type: 'contract',
 *   content: 'Lorem ipsum...'
 * };
 * const response = await apiCall('POST', 'https://api.example.com/documents', newDocument);
 * 
 * @example
 * // POST with file upload (multipart/form-data)
 * const formData = {
 *   title: 'New Document',
 *   file: fileInputElement.files[0],  // File object from <input type="file">
 *   category: 'contracts'
 * };
 * const response = await apiCall(
 *   'POST',
 *   'https://api.example.com/documents/upload',
 *   formData,
 *   'multipart/form-data'
 * );
 * 
 * @example
 * // Token refresh endpoint (uses refresh token)
 * const newTokens = await apiCall(
 *   'POST',
 *   'https://api.example.com/auth/refresh-access-token',
 *   {}
 * );
 */
export async function apiCall(method, apiUrl, data, content_type = 'application/json', authToken = localStorage.getItem("jsondata")) {
    try {
        // Parse authToken if present
        if (authToken) {
         
            // navigate("/login");
            // if(localStorage.login!==1){
            //     navigate("/login");
            //     return;
            // }
            const jsonData = JSON.parse(authToken);
            authToken = jsonData.accessToken || "";
            if (apiUrl.includes("refresh-access-token") || apiUrl.includes("logout")) {
                authToken = jsonData.refreshToken;
            }
        }
        
        // Set up the fetch options
        const fetchOptions = {
            method,
            headers: {
                'Authorization': 'Bearer ' + authToken,
            },
        };

        // Handle different content types
        if (method !== "GET") {
            if (content_type === 'application/json') {
                fetchOptions.headers['Content-Type'] = content_type;  // Set header only for JSON requests
                fetchOptions.body = JSON.stringify(data);
            } else if (content_type === 'multipart/form-data') {
                // Create FormData and append the data
                const formData = new FormData();
                
                // Append each key-value pair to FormData
                Object.keys(data).forEach((key) => {
                    formData.append(key, data[key]);
                });

                // For debugging: log the FormData contents
                for (let pair of formData.entries()) {
                    console.log(pair[0]+ ', ' + pair[1]);
                }

                fetchOptions.body = formData;
                // No need to manually set 'Content-Type' for multipart/form-data
            } else {
                // For any other content type, set the headers and body accordingly
                fetchOptions.headers['Content-Type'] = content_type;
                fetchOptions.body = data;
            }
        }

        // Make the fetch request
        const response = await fetch(apiUrl, fetchOptions);
        // Check for HTTP errors
        if(response.status===401){
            window.location.href="#/login"
            return;
        }
        if (!response.ok) {
            console.error(`Error: ${response.status} - ${response.statusText}`);
            trackError(`HTTP Error:<br/>URL:${apiUrl}<br/>Method:${method}<br>`, response.status);
        }

        // Parse and validate the response
        const responseData = await response.json();
        if (!isValidJson(JSON.stringify(responseData))) {
            trackError(`The API response is invalid:<br/>URL:${apiUrl}<br/>Method:${method}<br>`, JSON.stringify(responseData));
        }

        return responseData;
    } catch (error) {
        trackError(`The server API request was unsuccessful:<br/>URL:${apiUrl}<br/>Method:${method}<br>`, error);
    }
}



/**
 * Async wrapper for apiCall function
 * Provides a simplified async/await interface for making server API calls
 * Simply delegates to apiCall() and returns its promise
 * 
 * Use this function when you prefer explicit async/await syntax over then/catch chains
 * Functionally identical to apiCall() - all features and error handling are the same
 * 
 * @async
 * @param {string} method - HTTP method: 'GET', 'POST', 'PUT', 'DELETE', 'PATCH'
 * @param {string} apiUrl - Full API endpoint URL (including domain and path)
 * @param {Object} [data] - Request body data
 *                         For JSON: plain JavaScript object
 *                         For FormData: object with key-value pairs
 * @param {string} [content_type='application/json'] - Content type for request
 *                                                    Options: 'application/json', 'multipart/form-data'
 * @param {string} [authToken=localStorage.getItem("jsondata")] - Authorization token (optional)
 *                                                                 Defaults to stored token from login
 * 
 * @returns {Promise<Object>} Parsed response data from server
 *                           See apiCall() for detailed return value documentation
 * 
 * @throws Will not throw errors, instead tracks them via trackError()
 *         See apiCall() for error handling details
 * 
 * @see {@link apiCall} for complete implementation details and examples
 * 
 * @example
 * // Fetch documents using async/await syntax
 * try {
 *   const documents = await apiCallAsync('GET', 'https://api.example.com/documents');
 *   console.log('Documents:', documents);
 * } catch (error) {
 *   console.error('Failed to fetch documents:', error);
 * }
 * 
 * @example
 * // Create document with async/await
 * const newDocument = { title: 'New Contract', type: 'contract' };
 * const response = await apiCallAsync('POST', 'https://api.example.com/documents', newDocument);
 * console.log('Created document with ID:', response.id);
 */
export async function apiCallAsync(method, apiUrl, data, content_type='application/json',authToken = localStorage.getItem("jsondata")) {
    return await apiCall(method, apiUrl, data,content_type,authToken);
}

